// FixTheseFirstWidget.js
// PRD-130 WP3 — CTEM "Fix These First" widget for the Command Center.
//
// Consumes GET /api/alarm-prioritizer (PRD-130 WP2): shows the top-3 highest-
// priority open alarms with a 0–100 score + "why" chips (KEV / ransomware /
// EPSS / drift), an expandable full ranked list, and a RECOMMEND-ONLY tidy-up
// block that LINKS to the bulk-disposition flow on /drift-alarms.
//
// GUARDRAIL (recommend-only, PRD-130): this widget NEVER disposition/suppresses.
// The "suggest" affordance is a navigation link to the existing PRD-49 bulk flow
// — there is no POST/write of any kind from here.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAlarmPrioritizer } from '../../services/driftService';
import './FixTheseFirstWidget.css';

// Build the human-readable "why this ranks" chips from the scored factors.
// OQ-5 (drafted YES): KEV/EPSS signals ARE surfaced to customers.
function buildChips(factors = {}) {
    const chips = [];
    if (factors.kevListed) chips.push({ key: 'kev', label: 'KEV-listed', tone: 'critical' });
    if (factors.ransomware) chips.push({ key: 'ransomware', label: 'Ransomware', tone: 'critical' });
    if (factors.epssMax != null && factors.epssMax >= 0.01) {
        chips.push({ key: 'epss', label: `EPSS ${Math.round(factors.epssMax * 100)}%`, tone: 'high' });
    }
    if (factors.syntheticDrift) chips.push({ key: 'drift', label: 'Drift', tone: 'medium' });
    if (Number(factors.blastCount) > 0) {
        chips.push({ key: 'blast', label: `Blast ×${factors.blastCount}`, tone: 'info' });
    }
    return chips;
}

function scoreTone(score) {
    if (score >= 80) return 'critical';
    if (score >= 50) return 'high';
    if (score >= 25) return 'medium';
    return 'low';
}

function AlarmCard({ alarm, rank, onOpen }) {
    const chips = buildChips(alarm.factors);
    const tone = scoreTone(alarm.score);
    return (
        <div
            className="ftf-card"
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
        >
            <div className={`ftf-score ftf-score--${tone}`}>
                <span className="ftf-score-num">{alarm.score}</span>
                <span className="ftf-score-rank">#{rank}</span>
            </div>
            <div className="ftf-card-body">
                <div className="ftf-card-title">{alarm.name || alarm.findingId || 'Open alarm'}</div>
                <div className="ftf-card-meta">
                    <span className={`ftf-sev ftf-sev--${(alarm.severity || 'info').toLowerCase()}`}>
                        {(alarm.severity || 'info').toUpperCase()}
                    </span>
                    {alarm.matchedAt && <span className="ftf-host">{alarm.matchedAt}</span>}
                </div>
                {chips.length > 0 && (
                    <div className="ftf-chips">
                        {chips.map((c) => (
                            <span key={c.key} className={`ftf-chip ftf-chip--${c.tone}`}>{c.label}</span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function FixTheseFirstWidget() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getAlarmPrioritizer();
                if (!cancelled) setPayload(data && data.success ? data : null);
            } catch (err) {
                if (!cancelled) setError(err.message || 'Failed to load prioritization');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return <div className="ftf-loading">Prioritizing your open alarms…</div>;
    }
    if (error) {
        return <div className="ftf-error">{error}</div>;
    }

    const ranked = (payload && payload.ranked) || [];
    const topThree = (payload && payload.topThree) || [];
    const suggestions = (payload && payload.suggestions) || [];

    if (ranked.length === 0) {
        return (
            <div className="ftf-empty">
                <span className="ftf-empty-icon">✓</span>
                <p>No open alarms to prioritize — your attack surface is clean.</p>
            </div>
        );
    }

    const openQueue = () => navigate('/drift-alarms');
    const rest = ranked.slice(topThree.length);

    return (
        <div className="ftf-widget">
            <div className="ftf-cards">
                {topThree.map((a, i) => (
                    <AlarmCard key={a.alarmId || a.findingId || i} alarm={a} rank={i + 1} onOpen={openQueue} />
                ))}
            </div>

            {payload && payload.degraded && (
                <div className="ftf-degraded" title="Exploitability enrichment was unavailable; ranking used severity only.">
                    ⚠ Exploitability data (KEV/EPSS) was temporarily unavailable — ranking used severity only.
                </div>
            )}

            {rest.length > 0 && (
                <div className="ftf-more">
                    <button className="ftf-more-btn" onClick={() => setExpanded((v) => !v)}>
                        {expanded ? 'Hide' : `Show ${rest.length} more open alarm${rest.length === 1 ? '' : 's'}`}
                    </button>
                    {expanded && (
                        <ul className="ftf-list">
                            {rest.map((a, i) => {
                                const chips = buildChips(a.factors);
                                return (
                                    <li
                                        key={a.alarmId || a.findingId || i}
                                        className="ftf-list-row"
                                        role="button"
                                        tabIndex={0}
                                        onClick={openQueue}
                                        onKeyDown={(e) => { if (e.key === 'Enter') openQueue(); }}
                                    >
                                        <span className={`ftf-list-score ftf-list-score--${scoreTone(a.score)}`}>{a.score}</span>
                                        <span className="ftf-list-name">{a.name || a.findingId}</span>
                                        <span className="ftf-list-chips">
                                            {chips.slice(0, 3).map((c) => (
                                                <span key={c.key} className={`ftf-chip ftf-chip--${c.tone}`}>{c.label}</span>
                                            ))}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}

            {suggestions.length > 0 && (
                <div className="ftf-suggest">
                    <div className="ftf-suggest-head">
                        <span className="ftf-suggest-icon">🧹</span>
                        <span>
                            {suggestions.length} recurring low-risk alarm{suggestions.length === 1 ? '' : 's'} you may want to tidy up
                        </span>
                    </div>
                    <p className="ftf-suggest-note">
                        These are non-exploited, low-severity findings seen repeatedly. Review and bulk-accept them
                        on the Drift Alarms page — nothing is changed automatically.
                    </p>
                    <button className="ftf-suggest-link" onClick={() => navigate('/drift-alarms?severity=low')}>
                        Review on Drift Alarms →
                    </button>
                </div>
            )}
        </div>
    );
}

export default FixTheseFirstWidget;
